import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "./useProfile";
import { toast } from "sonner";
import { Database } from "@/integrations/supabase/types";

type SportType = Database['public']['Enums']['sport_type'];
type ClassType = Database['public']['Enums']['class_type'];
type ClassStatus = Database['public']['Enums']['class_status'];
type SkillLevel = Database['public']['Enums']['skill_level'];

export interface ClassSchedule {
  id: string;
  class_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  location: string | null;
  created_at: string;
}

export interface ClassEnrollment {
  id: string;
  class_id: string;
  student_id: string;
  enrollment_type: string;
  total_fee: number;
  payment_status: string;
  payment_deadline: string | null;
  status: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  student?: {
    id: string;
    name: string;
    avatar: string | null;
  };
  training_class?: TrainingClass;
}

export interface TrainingClass {
  id: string;
  coach_id: string;
  sport: SportType;
  class_type: ClassType;
  title: string;
  description: string | null;
  court_id: string | null;
  location: string;
  latitude: number | null;
  longitude: number | null;
  price_per_session: number;
  total_sessions: number;
  sessions_per_week: number | null;
  duration_weeks: number | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  max_students: number;
  current_students: number;
  status: ClassStatus;
  skill_level: SkillLevel | null;
  age_group: string | null;
  curriculum_url: string | null;
  curriculum_name: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
  coach?: {
    id: string;
    name: string;
    avatar: string | null;
  };
  court?: {
    id: string;
    name: string;
    location: string;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
  schedules?: ClassSchedule[];
}

export const useTrainingClasses = (filters?: {
  sport?: SportType;
  classType?: ClassType;
  coachId?: string;
}) => {
  return useQuery({
    queryKey: ['training-classes', filters],
    queryFn: async () => {
      let query = supabase
        .from('training_classes')
        .select(`
          *,
          coach:profiles!training_classes_coach_id_fkey(
            id, name, avatar,
            coach_profile:coach_profiles!coach_profiles_profile_id_fkey(is_verified)
          ),
          court:courts(id, name, location, latitude, longitude),
          schedules:class_schedules(*)
        `)
        .in('status', ['active', 'full'])
        .order('created_at', { ascending: false });

      if (filters?.sport) {
        query = query.eq('sport', filters.sport);
      }
      if (filters?.classType) {
        query = query.eq('class_type', filters.classType);
      }
      if (filters?.coachId) {
        query = query.eq('coach_id', filters.coachId);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // On public discovery pages (no coachId), hide expired & full classes
      if (!filters?.coachId) {
        const today = new Date().toISOString().split('T')[0];
        return (data as TrainingClass[]).filter(c => {
          if (c.status === 'full') return false;
          if (c.class_type === 'single_session' && c.date && c.date < today) return false;
          if (c.class_type === 'course' && c.end_date && c.end_date < today) return false;
          return true;
        });
      }
      
      return data as TrainingClass[];
    }
  });
};

export const useTrainingClassDetail = (classId: string) => {
  return useQuery({
    queryKey: ['training-class', classId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_classes')
        .select(`
          *,
          coach:profiles!training_classes_coach_id_fkey(id, name, avatar),
          court:courts(id, name, location, latitude, longitude),
          schedules:class_schedules(*)
        `)
        .eq('id', classId)
        .single();

      if (error) throw error;
      return data as TrainingClass;
    },
    enabled: !!classId
  });
};

export const useMyTrainingClasses = () => {
  const { profile } = useProfile();

  return useQuery({
    queryKey: ['my-training-classes', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      const { data, error } = await supabase
        .from('training_classes')
        .select(`
          *,
          coach:profiles!training_classes_coach_id_fkey(id, name, avatar),
          court:courts(id, name, location, latitude, longitude),
          schedules:class_schedules(*)
        `)
        .eq('coach_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as TrainingClass[];
    },
    enabled: !!profile?.id
  });
};

export const useCreateTrainingClass = () => {
  const queryClient = useQueryClient();
  const { profile } = useProfile();

  return useMutation({
    mutationFn: async (classData: {
      sport: SportType;
      class_type: ClassType;
      title: string;
      description?: string;
      court_id?: string;
      location: string;
      latitude?: number;
      longitude?: number;
      price_per_session: number;
      total_sessions?: number;
      sessions_per_week?: number;
      duration_weeks?: number;
      date?: string;
      start_time?: string;
      end_time?: string;
      max_students: number;
      skill_level?: SkillLevel;
      age_group?: string;
      schedules?: Omit<ClassSchedule, 'id' | 'class_id' | 'created_at'>[];
      curriculum_url?: string;
      curriculum_name?: string;
      start_date?: string;
      end_date?: string;
    }) => {
      if (!profile?.id) throw new Error('Chưa đăng nhập');

      // Pre-check coach role before attempting insert
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Chưa đăng nhập');

      const { data: coachRole } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'coach')
        .maybeSingle();

      if (!coachRole) {
        throw new Error('Bạn cần đăng ký làm HLV trước khi tạo lớp học. Vui lòng vào trang Hồ sơ để đăng ký.');
      }

      const { schedules, curriculum_url, curriculum_name, start_date, end_date, latitude, longitude, ...classInfo } = classData;

      // Build insert data object, only including defined values to avoid "null" string issues
      const insertData: Record<string, any> = {
        coach_id: profile.id,
        sport: classInfo.sport,
        class_type: classInfo.class_type,
        title: classInfo.title,
        location: classInfo.location,
        price_per_session: classInfo.price_per_session,
        max_students: classInfo.max_students,
      };

      // Only add optional fields if they have actual values
      if (classInfo.description) insertData.description = classInfo.description;
      if (classInfo.court_id) insertData.court_id = classInfo.court_id;
      if (classInfo.total_sessions) insertData.total_sessions = classInfo.total_sessions;
      if (classInfo.sessions_per_week) insertData.sessions_per_week = classInfo.sessions_per_week;
      if (classInfo.duration_weeks) insertData.duration_weeks = classInfo.duration_weeks;
      if (classInfo.date) insertData.date = classInfo.date;
      if (classInfo.start_time) insertData.start_time = classInfo.start_time;
      if (classInfo.end_time) insertData.end_time = classInfo.end_time;
      if (classInfo.skill_level) insertData.skill_level = classInfo.skill_level;
      if (classInfo.age_group) insertData.age_group = classInfo.age_group;
      if (curriculum_url) insertData.curriculum_url = curriculum_url;
      if (curriculum_name) insertData.curriculum_name = curriculum_name;
      if (start_date) insertData.start_date = start_date;
      if (end_date) insertData.end_date = end_date;
      if (latitude != null) insertData.latitude = latitude;
      if (longitude != null) insertData.longitude = longitude;

      const { data: newClass, error: classError } = await supabase
        .from('training_classes')
        .insert(insertData as Database['public']['Tables']['training_classes']['Insert'])
        .select()
        .single();

      if (classError) throw classError;

      // Insert schedules if provided
      if (schedules && schedules.length > 0) {
        const { error: schedulesError } = await supabase
          .from('class_schedules')
          .insert(
            schedules.map(s => ({
              ...s,
              class_id: newClass.id
            }))
          );

        if (schedulesError) throw schedulesError;
      }

      return newClass;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-training-classes'] });
      queryClient.invalidateQueries({ queryKey: ['training-classes'] });
      // Toast with action is handled in CreateClassDialog
    },
    onError: (error: any) => {
      console.error('Create class error:', error);
      
      // Handle coach role error specifically
      if (error.message?.includes('HLV') || error.message?.includes('coach')) {
        toast.error(error.message);
        return;
      }
      
      // Handle RLS policy rejection
      if (error?.code === '42501' || error.message?.includes('policy')) {
        toast.error('Bạn không có quyền tạo lớp học. Vui lòng đăng ký làm HLV.');
        return;
      }
      
      // Parse PostgreSQL error codes for better messages
      if (error?.code === '23505') {
        if (error.message?.includes('class_schedules')) {
          toast.error('Lịch học bị trùng. Vui lòng chọn khung giờ khác.');
        } else {
          toast.error('Dữ liệu bị trùng lặp.');
        }
      } else if (error?.code === '23514') {
        if (error.message?.includes('price_non_negative')) {
          toast.error('Giá học phí không được âm.');
        } else if (error.message?.includes('max_students_min')) {
          toast.error('Số học viên tối đa phải >= 1.');
        } else if (error.message?.includes('valid_time')) {
          toast.error('Giờ kết thúc phải sau giờ bắt đầu.');
        } else {
          toast.error('Dữ liệu không hợp lệ: ' + error.message);
        }
      } else {
        toast.error(error.message || 'Không thể tạo lớp học');
      }
    }
  });
};

export const useUpdateTrainingClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      classId,
      updates
    }: {
      classId: string;
      updates: Partial<TrainingClass>;
    }) => {
      const { error } = await supabase
        .from('training_classes')
        .update(updates)
        .eq('id', classId);

      if (error) throw error;
    },
    onSuccess: (_, { classId }) => {
      queryClient.invalidateQueries({ queryKey: ['my-training-classes'] });
      queryClient.invalidateQueries({ queryKey: ['training-classes'] });
      queryClient.invalidateQueries({ queryKey: ['training-class', classId] });
      toast.success('Cập nhật lớp học thành công!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Không thể cập nhật lớp học');
    }
  });
};

export const useDeleteTrainingClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (classId: string) => {
      // Check for active enrollments before deleting
      const { data: activeEnrollments, error: checkError } = await supabase
        .from('class_enrollments')
        .select('id')
        .eq('class_id', classId)
        .in('status', ['approved', 'pending'])
        .limit(1);

      if (checkError) throw checkError;

      if (activeEnrollments && activeEnrollments.length > 0) {
        throw new Error('Không thể xóa lớp đang có học viên đăng ký. Hãy hủy lớp thay vì xóa.');
      }

      // BUG 23 FIX: Check for active escrow payments linked to this class's enrollments
      const { data: allEnrollments } = await supabase
        .from('class_enrollments')
        .select('id')
        .eq('class_id', classId);

      if (allEnrollments && allEnrollments.length > 0) {
        const enrollmentIds = allEnrollments.map(e => e.id);
        const { data: activeEscrows } = await supabase
          .from('escrow_payments')
          .select('id')
          .eq('activity_type', 'training')
          .in('activity_id', enrollmentIds)
          .in('escrow_status', ['held', 'disputed'])
          .limit(1);

        if (activeEscrows && activeEscrows.length > 0) {
          throw new Error('Không thể xóa lớp đang có giao dịch tài chính chưa hoàn tất.');
        }
      }

      const { error } = await supabase
        .from('training_classes')
        .delete()
        .eq('id', classId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-training-classes'] });
      queryClient.invalidateQueries({ queryKey: ['training-classes'] });
      toast.success('Xóa lớp học thành công!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Không thể xóa lớp học');
    }
  });
};
